/**
 * Casca mobile do perfil Manutenção (celular e tablet).
 *
 * Substitui o `AppShell` (menu lateral fixo de 256 px, que não cabe no celular)
 * em volta das MESMAS rotas do módulo. Mesma identidade do SIAG e do Portal:
 * cabeçalho `tm-900`, verde Transmasi, cards e botões do sistema.
 * - celular e tablet em pé (< lg): barra inferior com Central, Preventiva, O.S.,
 *   Checklists e "Mais" (o resto do módulo, numa folha que sobe de baixo);
 * - tablet deitado (lg+): todas as telas em abas no topo.
 */
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import {
  IconBarChart, IconCalendar, IconChevronLeft, IconChevronRight, IconClipboard, IconDocument, IconDollar,
  IconHome, IconLogout, IconMonitor, IconMore, IconPlus, IconSearch, IconShield, IconTruck, IconWrench, IconX,
} from "../../components/icons";
import {
  activeBottomKey, activeTab, MOBILE_BOTTOM_NAV, MOBILE_MORE_ITEMS, MOBILE_TABS, screenInfo,
  type MobileNavKey, type MoreItem,
} from "./mobile-layout";
import { useMobileLayout } from "./useMobileLayout";

const ICONE_BARRA: Record<MobileNavKey, React.ComponentType<{ className?: string }>> = {
  central: IconHome,
  preventiva: IconCalendar,
  ordens: IconClipboard,
  checklists: IconDocument,
  mais: IconMore,
};

const ICONE_MAIS: Record<MoreItem["icon"], React.ComponentType<{ className?: string }>> = {
  register: IconPlus,
  shield: IconShield,
  erp: IconWrench,
  templates: IconClipboard,
  groups: IconTruck,
  costs: IconDollar,
};

export function MaintenanceMobileShell() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { switchTo } = useMobileLayout();
  const [maisAberto, setMaisAberto] = useState(false);

  // Letra e alvos de toque maiores em toda a tela (ver `html.siag-mobile` em globals.css).
  // A classe sai quando a casca sai — ao trocar para a versão de computador.
  useEffect(() => {
    document.documentElement.classList.add("siag-mobile");
    return () => document.documentElement.classList.remove("siag-mobile");
  }, []);

  // Navegou: a folha do "Mais" fecha e a tela nova começa do topo.
  useEffect(() => { setMaisAberto(false); window.scrollTo(0, 0); }, [pathname]);

  const tela = screenInfo(pathname);
  const ativo = maisAberto ? "mais" : activeBottomKey(pathname);
  const aba = activeTab(pathname);

  const voltar = () => {
    // Entrou direto pelo link (sem histórico no app): volta para a lista da seção.
    const temHistorico = (window.history.state as { idx?: number } | null)?.idx;
    if (temHistorico) navigate(-1);
    else navigate(pathname.replace(/\/[^/]+$/, "") || "/frota/manutencao");
  };

  // overflow-x-clip: uma tela ainda larga demais não pode fazer o navegador do
  // celular "afastar o zoom" da página inteira — aí a barra inferior sai da tela.
  // `clip` (e não `hidden`) para não virar contêiner de rolagem e não quebrar o sticky.
  return (
    <div className="min-h-screen overflow-x-clip bg-surface-muted" data-layout="mobile">
      <header className="sticky top-0 z-30 bg-tm-900 text-white pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-2 px-3 lg:px-5">
          {tela.child ? (
            <button type="button" onClick={voltar} aria-label="Voltar"
              className="-ml-1 grid h-11 w-11 place-items-center rounded-lg hover:bg-white/5">
              <IconChevronLeft className="h-6 w-6" />
            </button>
          ) : (
            <span className="ml-1 grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-tm-500">
              <IconWrench className="h-[1.125rem] w-[1.125rem] text-white" />
            </span>
          )}
          <div className={`min-w-0 flex-1 ${tela.child ? "" : "ml-1"}`}>
            <div className="truncate text-[0.9375rem] font-bold leading-tight">{tela.title}</div>
            <div className="truncate text-xs leading-tight text-tm-300">{user?.name ?? ""} · Manutenção</div>
          </div>
          <button type="button" onClick={() => switchTo("desktop")}
            className="hidden min-h-[2.75rem] items-center gap-1.5 rounded-lg px-2 text-sm text-tm-100/80 hover:bg-white/5 hover:text-white lg:inline-flex">
            <IconMonitor className="h-4 w-4" /> Versão para computador
          </button>
        </div>
      </header>

      {/* Abas — tablet deitado */}
      <nav className="sticky top-14 z-20 hidden border-b border-surface-border bg-white lg:block" aria-label="Manutenção">
        <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-5">
          {MOBILE_TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end
              className={`-mb-px whitespace-nowrap border-b-2 px-3 py-3 text-sm transition-colors ${
                aba === t.to ? "border-tm-500 font-semibold text-tm-700" : "border-transparent text-gray-500 hover:text-gray-900"
              }`}>
              {t.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <main className="mx-auto min-w-0 max-w-6xl px-4 pb-28 pt-4 lg:px-5 lg:pb-10">
        <Outlet />
      </main>

      {/* Barra inferior — celular e tablet em pé */}
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-surface-border bg-white pb-[env(safe-area-inset-bottom)] lg:hidden" aria-label="Manutenção">
        <div className="grid grid-cols-5">
          {MOBILE_BOTTOM_NAV.map((item) => {
            const Icone = ICONE_BARRA[item.key];
            const on = ativo === item.key;
            const classe = `flex min-h-[3.625rem] flex-col items-center justify-center gap-0.5 text-[0.6875rem] ${on ? "font-semibold text-tm-600" : "text-gray-500"}`;
            const conteudo = <><Icone className={`h-5 w-5 ${on ? "text-tm-500" : "text-gray-400"}`} />{item.label}</>;
            return item.key === "mais" ? (
              <button key={item.key} type="button" className={classe} aria-expanded={maisAberto} onClick={() => setMaisAberto((v) => !v)}>
                {conteudo}
              </button>
            ) : (
              <NavLink key={item.key} to={item.to} end={item.key === "central"} className={classe}>{conteudo}</NavLink>
            );
          })}
        </div>
      </nav>

      {maisAberto && <FolhaMais onFechar={() => setMaisAberto(false)} onComputador={() => switchTo("desktop")} />}
    </div>
  );
}

/** "Mais": o resto do módulo, a busca por placa e a troca para o computador. */
function FolhaMais({ onFechar, onComputador }: { onFechar: () => void; onComputador: () => void }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [placa, setPlaca] = useState("");
  const canonica = placa.toUpperCase().replace(/[^A-Z0-9]/g, "");

  return (
    <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Mais opções">
      <div className="absolute inset-0 bg-gray-900/40" onClick={onFechar} />
      <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-surface-muted pb-[calc(env(safe-area-inset-bottom)+4.5rem)]">
        <div className="sticky top-0 flex items-center justify-between bg-surface-muted px-4 pb-2 pt-3">
          <span className="text-base font-semibold text-gray-900">Mais</span>
          <button type="button" className="grid h-11 w-11 place-items-center rounded-lg text-gray-500" onClick={onFechar} aria-label="Fechar">
            <IconX className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 px-4">
          <form className="card flex items-center gap-2 p-2.5"
            onSubmit={(e) => { e.preventDefault(); if (canonica.length >= 7) navigate(`/frota/manutencao/placa/${canonica}`); }}>
            <IconSearch className="ml-1 h-5 w-5 flex-shrink-0 text-gray-400" />
            <input className="input flex-1 !border-0 !px-1 font-mono uppercase !ring-0" placeholder="Histórico por placa" value={placa}
              maxLength={8} autoCapitalize="characters" onChange={(e) => setPlaca(e.target.value)} />
            <button type="submit" className="btn-secondary !px-3" disabled={canonica.length < 7}>Abrir</button>
          </form>

          <div className="card divide-y divide-surface-border">
            {MOBILE_MORE_ITEMS.map((i) => {
              const Icone = ICONE_MAIS[i.icon];
              return (
                <NavLink key={i.to} to={i.to} className="flex min-h-[3.5rem] items-center gap-3 px-3.5 py-2.5">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-tm-50 text-tm-600"><Icone className="h-[1.125rem] w-[1.125rem]" /></span>
                  <span className="flex-1 text-sm font-medium text-gray-900">{i.label}</span>
                  <IconChevronRight className="h-4 w-4 text-gray-400" />
                </NavLink>
              );
            })}
          </div>

          <div className="card divide-y divide-surface-border">
            <button type="button" onClick={onComputador} className="flex min-h-[3.5rem] w-full items-center gap-3 px-3.5 py-2.5 text-left">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-gray-100 text-gray-600"><IconMonitor className="h-[1.125rem] w-[1.125rem]" /></span>
              <span className="flex-1 text-sm font-medium text-gray-900">Versão para computador</span>
              <IconChevronRight className="h-4 w-4 text-gray-400" />
            </button>
            <button type="button" onClick={signOut} className="flex min-h-[3.5rem] w-full items-center gap-3 px-3.5 py-2.5 text-left">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-danger-50 text-danger-600"><IconLogout className="h-[1.125rem] w-[1.125rem]" /></span>
              <span className="flex-1 text-sm font-medium text-danger-600">Sair</span>
            </button>
          </div>
          <p className="pb-2 text-center text-xs text-gray-400"><IconBarChart className="mr-1 inline h-3.5 w-3.5 align-[-0.125rem]" />SIAG · Transmasi</p>
        </div>
      </div>
    </div>
  );
}
